import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { BusinessUnitModel } from '../../models/business-unit';
import { BusinessUnitService } from '../../services/business-unit.service';
import { CancelEvent, EditEvent, GridComponent, SaveEvent } from '@progress/kendo-angular-grid';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { EventBusService } from '../../../../services/event-bus.service';

/**
 * Business Unit administration
 */
@Component({
  selector: 'admin-business-unit',
  templateUrl: './admin-business-unit.component.html',
  styleUrls: ['./admin-business-unit.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class AdminBusinessUnitComponent implements OnInit, OnDestroy {

  /**
* Data displayed in the grid
*/
  public gridData: BusinessUnitModel[] = [];
  /**
* Selected row items
*/
  public mySelection: number[] = [];
  /**
* Flag if edit mode
*/
  public isEditMode = false;
  /**
* BU grid element
*/
  @ViewChild("buGrid") buGrid: GridComponent;

  /**
* Form group used for adding/editing grid row
*/
  public formGroup: UntypedFormGroup;
  /**
  * Edited row index
  */
  private editedRowIndex: number;

  /**
 * The (list of) subscription(s)
 */
  subscriptions: Subscription = new Subscription();

  /**
 * Constructor
 */
  constructor(private buService: BusinessUnitService, private ref: ChangeDetectorRef, private eventBus: EventBusService) { }

  /**
   * Destroys the component
   */
  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  /**
* On Init
*/
  ngOnInit(): void {
    this.loadData();
    this.buService.addNewBU$.subscribe(data => {
      if (data) {
        this.addHandler();
      }
    })
  }

  /**
* Loads data
*/
  private loadData() {
    this.buService.getBUs().subscribe(data => {
      this.gridData = data.filter(bu => bu.oid > 0);
      this.ref.markForCheck();
    })

  }

  /**
  * Grid handler for adding new row
  */
  public addHandler(): void {
    this.closeEditor(this.buGrid);
    // define all editable fields validators and default values
    this.formGroup = new UntypedFormGroup({
      oid: new UntypedFormControl(),
      systemName: new UntypedFormControl("", Validators.required),
      displayName: new UntypedFormControl("", Validators.required),
      description: new UntypedFormControl("", Validators.required),
      logdrive: new UntypedFormControl("", Validators.required),
      active: new UntypedFormControl(false),
      isRole: new UntypedFormControl(false),
    });
    // show the new row editor, with the `FormGroup` build above
    this.buGrid.addRow(this.formGroup);
    this.ref.markForCheck();
  }

  /**
* Grid handler for editing row
*/
  public editHandler(args: EditEvent): void {
    // define all editable fields validators and default values
    const { dataItem } = args;
    this.closeEditor(args.sender);
    this.formGroup = new UntypedFormGroup({
      oid: new UntypedFormControl(dataItem.oid),
      systemName: new UntypedFormControl(dataItem.systemName),
      displayName: new UntypedFormControl(dataItem.displayName),
      description: new UntypedFormControl(dataItem.description),
      logdrive: new UntypedFormControl(dataItem.logdrive),
      active: new UntypedFormControl(dataItem.active),
      isRole: new UntypedFormControl(dataItem.isRole),

    });

    this.editedRowIndex = args.rowIndex;
    // put the row in edit mode, with the `FormGroup` build above
    args.sender.editRow(args.rowIndex, this.formGroup);
  }

  /**
* Close the editor for the given row
*/
  public cancelHandler(args: CancelEvent): void {
    this.closeEditor(args.sender, args.rowIndex);
  }

  /**
* grid handler for saving data
*/
  public saveHandler({ sender, rowIndex, formGroup, isNew }: SaveEvent): void {
    const bu: BusinessUnitModel = formGroup.value;

    this.buService.updateBU(bu).subscribe(result => {

      sender.closeRow(rowIndex);

      let message: string;
      if (result.m_Item1) {
        message = 'buManagementApplyOnReload';
      }
      else if (result.m_Item2 == "SystemName is null or empty!") {
        message = 'buManagementNoSystemName';
      }
      else if (result.m_Item2 == "DisplayName is null or empty!") {
        message = 'buManagementNoDisplayName';
      }
      else if (result.m_Item2 == "Logdrive is null or empty!") {
        message = 'buManagementNoLogdrive';
      }
      else if (result.m_Item2 == "Duplicate entry!") {
        message = 'buManagementDuplicateEntry';
      }

      this.eventBus.broadcastEvent("openConfirmDialog", {
        success: () => {
          //Nothing
        }
      }, message);

      this.loadData();

    })
  }

  /**
  * Close grid editor
  */
  private closeEditor(grid: GridComponent, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }
}
